import {
  formatMoney,
  isSameAmount,
  MONEY_SCALE,
  parseHumanAmount,
  toMoney,
  toString,
  UNIT_PRICE_SCALE,
  type Decimal,
} from '@aeris/money'
import { parseUnitPrice } from '@aeris/pricing'

/**
 * Money, on screen.
 *
 * Formatting goes through `@aeris/money` rather than `Intl.NumberFormat`,
 * because `Intl` takes a `number` and the whole point of ADR-0016 is that a
 * price never becomes one. A screen and a printed document have to agree about
 * the last centavo, and they only can if they format the same exact value.
 */

/**
 * `R$ 1.234,56` — or more decimals when the price genuinely has them.
 *
 * A unit price carries six decimals (`vUnCom` in the NF-e layout permits it,
 * and R$ 0,0325 per unit is ordinary in wholesale). Rounding those away on
 * screen would make the field lie about what a thousand units will cost, so
 * the extra digits are shown when they are not zero and hidden when they are.
 */
export const formatUnitPrice = (wire: string): string => {
  const value = parseUnitPrice(wire)
  const centavos = toMoney(value)

  if (isSameAmount(centavos, value)) return formatMoney(centavos)

  const [whole = '0', fraction = ''] = toString(value).split('.')
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  return `R$ ${grouped},${fraction.replace(/0+$/, '')}`
}

/** What goes into an input when somebody starts editing: `1234,56`, no symbol. */
export const toEditableAmount = (wire: string | null): string => {
  if (wire === null) return ''
  const text = toString(parseUnitPrice(wire))
  const [whole = '0', fraction = ''] = text.split('.')
  const trimmed = fraction.replace(/0+$/, '')
  return trimmed.length === 0 ? whole : `${whole},${trimmed}`
}

/**
 * Reads what a person typed, at the unit-price scale.
 *
 * `parseHumanAmount` and not `fromString`: a field is not a protocol.
 * `1.234,56`, `1234,56` and `R$ 12,50` all mean what the person meant, and an
 * unfinished value returns null rather than throwing, because an empty field
 * is a normal state while somebody is typing.
 */
export const parseTypedPrice = (input: string): Decimal | null =>
  parseHumanAmount(input, UNIT_PRICE_SCALE)

/**
 * Reads what a person typed, at the MONEY scale.
 *
 * Separate from `parseTypedPrice` because the two scales are not
 * interchangeable on the wire: a unit price carries six decimals and a document
 * amount carries two, and the finance contracts refuse `450.000000` by regex.
 * Reading a título's amount at the price scale would produce a value the server
 * rejects for a reason nobody could guess from the screen.
 */
export const parseTypedMoney = (input: string): Decimal | null =>
  parseHumanAmount(input, MONEY_SCALE)

/** A quantity typed into the simulator. Same rules, same package. */
export const parseTypedQuantity = (input: string): Decimal | null =>
  parseHumanAmount(input, UNIT_PRICE_SCALE)

export const toWireAmount = (value: Decimal): string => toString(value)

/** `5` rather than `5,000000`, for a tier heading a person reads. */
export const formatQuantity = (wire: string): string => {
  const text = toString(parseUnitPrice(wire))
  const [whole = '0', fraction = ''] = text.split('.')
  const trimmed = fraction.replace(/0+$/, '')
  return trimmed.length === 0 ? whole : `${whole},${trimmed}`
}

/**
 * Why no price applied, in Portuguese.
 *
 * The domain package returns a reason code and never a sentence: a package the
 * browser and the server both import must not carry an interface language, and
 * a code is what a caller branches on. The sentence belongs here.
 */
// Mudou para `@aeris/status-meta` na F306: o celular diz a mesma frase.
export { UNRESOLVED_PRICE_TEXT as REASON_LABELS } from '@aeris/status-meta'

/** Why a tier did or did not decide the price, in Portuguese. */
export const OUTCOME_LABELS: Record<string, string> = {
  applied: 'Aplicada',
  'quantity-below-minimum': 'Quantidade abaixo do mínimo',
  'not-yet-valid': 'Ainda não vigente',
  expired: 'Vigência encerrada',
  archived: 'Removida',
  'superseded-by-more-specific-tier': 'Substituída por faixa mais específica',
}
